import { useEffect, useState } from 'react';
import { WEBHOOK_KINDS, type TelegramChatDto, type WebhookCreateInput, type WebhookDeliveryDto, type WebhookEndpointDto, type WebhookKind } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { dateTime } from '@/lib/format';
import { GridIcon, PlusIcon } from '@/components/icons';
import { ListCard, Segmented, Sheet, TableScroll, Table, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Field, Input, Pill, Switch, TableSkeleton, cx } from '@/components/ui';
import { SettingsPage } from '@/pages/settings';
import { labelOr } from '@/lib/labels';
import type { MessageKey } from '@/i18n/messages';

/** An event as a person reads it — "Order placed" — with its code for whoever builds the receiver. */
function eventLabel(t: (key: MessageKey) => string, event: string): string {
  return labelOr(t, `webhookEvent_${event.replace(/\./g, '_')}`, event);
}

/**
 * Telling other systems — and the team — what happened (ADR-032, ADR-110).
 *
 * The screen is built around the question an integration argument always starts
 * with — *did you send it?* — so the delivery log is one click from every row,
 * and a paused endpoint says why rather than just looking off. A destination is
 * a signed JSON webhook, a Slack channel or a Telegram chat; adding or editing
 * one and reading its log open in a panel beside the list.
 */
export function WebhooksPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  const [endpoints, setEndpoints] = useState<WebhookEndpointDto[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpointDto | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpointDto | null>(null);
  const [history, setHistory] = useState<{ endpoint: WebhookEndpointDto; rows: WebhookDeliveryDto[] } | null>(null);

  const load = async () => {
    try {
      const [page, catalogue] = await Promise.all([
        withSession(() => adminApi.webhooks.list()),
        withSession(() => adminApi.webhooks.events()),
      ]);
      setEndpoints(page.items);
      setEvents(catalogue.events);
    } catch (cause) {
      setError(errorMessage(cause, 'Could not load the webhooks.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const act = async (run: () => Promise<unknown>, after?: string) => {
    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      await run();
      if (after) setNotice(after);
      await load();
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPage
      title="webhooks"
      subtitle="settingsWebhooksHint"
      fill
      actions={
        <Button onClick={() => setCreating(true)}>
          <PlusIcon className="size-4" />
          {t('addWebhook')}
        </Button>
      }
    >
      {error ? <Alert>{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <ListCard>
        {loading ? (
          <TableSkeleton rows={3} columns={3} />
        ) : endpoints.length === 0 ? (
          <EmptyState title={t('noWebhooks')} body={t('noWebhooksHint')} icon={<GridIcon />} />
        ) : (
          <TableScroll>
            <ul className="divide-y divide-line">
              {endpoints.map((endpoint) => (
                <li key={endpoint.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink">{endpoint.name}</span>
                        <Pill tone="info">{t(`webhookKind_${endpoint.kind}` as MessageKey)}</Pill>
                        {endpoint.pausedAt ? (
                          /* Paused says why: ten failures in a row is a URL that
                             moved, and "inactive" alone would look deliberate. */
                          <Pill tone="danger">{t('pausedAfterFailures')}</Pill>
                        ) : endpoint.isActive ? (
                          <Pill tone="success">{t('enabled')}</Pill>
                        ) : (
                          <Pill tone="neutral">{t('deactivated')}</Pill>
                        )}
                        {endpoint.failures > 0 && !endpoint.pausedAt ? (
                          <Pill tone="warning">
                            {t('recentFailures')}: {endpoint.failures}
                          </Pill>
                        ) : null}
                      </div>

                      <p dir="ltr" className="mt-1 truncate font-mono text-xs text-ink-muted">
                        {endpoint.url}
                        {endpoint.chatId ? ` → ${endpoint.chatId}` : ''}
                      </p>
                      <p className="mt-1 text-xs text-ink-muted">{endpoint.events.map((event) => eventLabel(t, event)).join(' · ')}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={saving}
                        onClick={() => void act(() => adminApi.webhooks.test(endpoint.id), t('testQueued'))}
                      >
                        {t('sendTest')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          const page = await withSession(() =>
                            adminApi.webhooks.deliveries(endpoint.id, { limit: 50 }),
                          );
                          setHistory({ endpoint, rows: page.items });
                        }}
                      >
                        {t('history')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(endpoint)}>
                        {t('editEntry')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleting(endpoint)}>
                        {t('deleteEntry')}
                      </Button>
                      <span className="ms-2">
                        <Switch
                          label={`${t('enabled')} — ${endpoint.name}`}
                          checked={endpoint.isActive}
                          disabled={saving}
                          onChange={(isActive) => void act(() => adminApi.webhooks.update(endpoint.id, { isActive }))}
                        />
                      </span>
                    </div>
                  </div>

                  {endpoint.secret ? (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-ink-muted">{t('signingSecret')}</summary>
                    {/* Shared by design, unlike an API key: the receiver needs it to
                        verify us, so it stays readable to an owner. */}
                    <p dir="ltr" className="mt-2 select-all break-all font-mono text-xs text-ink">
                      {endpoint.secret}
                    </p>
                    <p className="mt-1 text-xs text-ink-muted">{t('signingSecretHint')}</p>
                  </details>
                  ) : null}
                </li>
              ))}
            </ul>
          </TableScroll>
        )}
      </ListCard>

      {creating || editing ? (
        <DestinationSheet
          events={events}
          endpoint={editing}
          saving={saving}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={async (input) => {
            const id = editing?.id;
            await act(
              () => (id ? adminApi.webhooks.update(id, input) : adminApi.webhooks.create(input)),
              t(id ? 'webhookUpdated' : 'webhookCreated'),
            );
            setCreating(false);
            setEditing(null);
          }}
        />
      ) : null}

      <Sheet
        open={history !== null}
        size="lg"
        title={history ? `${t('history')} — ${history.endpoint.name}` : t('history')}
        onClose={() => setHistory(null)}
      >
        {history && history.rows.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('noDeliveries')}</p>
        ) : history ? (
          <div className="-mx-5 -mt-5">
            <Table>
              <thead>
                <tr>
                  <Th>{t('when')}</Th>
                  <Th>{t('auditAction')}</Th>
                  <Th>{t('result')}</Th>
                  <Th align="end" />
                </tr>
              </thead>
              <tbody>
                {history.rows.map((row) => (
                  <Tr key={row.id}>
                    <Td className="text-xs whitespace-nowrap text-ink-muted">{dateTime(row.createdAt, locale)}</Td>
                    <Td className="text-xs text-ink">
                      {eventLabel(t, row.event)}
                      {row.attempt > 1 ? (
                        <span className="text-ink-faint">
                          {' '}
                          · {t('attempt')} {row.attempt}
                        </span>
                      ) : null}
                    </Td>
                    <Td className="text-xs">
                      <span data-numeric className={cx(row.succeeded ? 'text-success' : 'text-danger')}>
                        {/* A timeout has no status code, which is exactly the
                            failure a status-only log cannot explain. */}
                        {row.statusCode ?? t('noResponse')}
                      </span>
                      {row.durationMs !== null ? (
                        <span data-numeric className="ms-2 text-ink-faint">
                          {row.durationMs}ms
                        </span>
                      ) : null}
                      {row.error ? <span className="ms-2 text-ink-muted">{row.error}</span> : null}
                    </Td>
                    <Td align="end">
                      {/*
                        For after the other end is repaired: the automatic
                        retries have a ceiling, which is right until a person
                        fixes the receiver — and then it is exactly wrong.
                      */}
                      {!row.succeeded ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={saving}
                          onClick={() => void act(() => adminApi.webhooks.resend(row.id), t('resendQueued'))}
                        >
                          {t('sendAgain')}
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </Sheet>

      <ConfirmDialog
        open={deleting !== null}
        title={t('deleteWebhook')}
        body={t('deleteWebhookBody')}
        confirmLabel={t('deleteEntry')}
        cancelLabel={t('cancel')}
        busy={saving}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await act(() => adminApi.webhooks.remove(deleting.id));
          setDeleting(null);
        }}
      />
    </SettingsPage>
  );
}

/**
 * Adding or changing a destination. Its kind is chosen once: a JSON receiver's
 * address, a Slack channel's incoming webhook, or a Telegram bot and chat —
 * the last two kept encrypted, so editing leaves an empty field as "keep".
 */
function DestinationSheet({
  events,
  endpoint,
  saving,
  onClose,
  onSave,
}: {
  events: string[];
  endpoint: WebhookEndpointDto | null;
  saving: boolean;
  onClose: () => void;
  onSave: (input: WebhookCreateInput) => Promise<void>;
}) {
  const t = useT();
  const [kind, setKind] = useState<WebhookKind>(endpoint?.kind ?? 'JSON');
  const [name, setName] = useState(endpoint?.name ?? '');
  const [url, setUrl] = useState(endpoint?.kind === 'JSON' ? endpoint.url : '');
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState(endpoint?.chatId ?? '');
  const [locale, setLocale] = useState<'ar' | 'en'>(endpoint?.locale ?? 'ar');
  const [selected, setSelected] = useState<string[]>(endpoint?.events ?? []);
  const [chats, setChats] = useState<TelegramChatDto[] | null>(null);
  const [finding, setFinding] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const editing = endpoint !== null;
  // A saved credential stays unless a new one is typed.
  const credentialReady =
    kind === 'JSON' ? url.trim().length > 0 : kind === 'SLACK' ? editing || url.trim().length > 0 : (editing || botToken.trim().length > 0) && chatId.trim().length > 0;
  const ready = name.trim().length >= 2 && selected.length > 0 && credentialReady;

  // The catalogue grouped by its area — orders, returns, stock… — in the server's order.
  const groups = events.reduce<{ area: string; events: string[] }[]>((list, event) => {
    const area = event.split('.')[0] ?? event;
    const group = list.find((entry) => entry.area === area);
    if (group) group.events.push(event);
    else list.push({ area, events: [event] });
    return list;
  }, []);

  async function findChats() {
    setFinding(true);
    setChatError(null);
    try {
      const page = await withSession(() => adminApi.webhooks.telegramChats(botToken));
      setChats(page.items);
    } catch (cause) {
      setChatError(errorMessage(cause, 'Telegram did not answer.'));
    } finally {
      setFinding(false);
    }
  }

  function submit() {
    const input: WebhookCreateInput = {
      ...(editing ? {} : { kind }),
      name: name.trim(),
      events: selected,
      ...(kind === 'JSON' ? { url: url.trim() } : {}),
      ...(kind === 'SLACK' && url.trim() ? { url: url.trim() } : {}),
      ...(kind === 'TELEGRAM' ? { chatId: chatId.trim(), ...(botToken.trim() ? { botToken: botToken.trim() } : {}) } : {}),
      ...(kind === 'JSON' ? {} : { locale }),
    };
    void onSave(input);
  }

  return (
    <Sheet
      open
      size="lg"
      title={editing ? t('editWebhook') : t('addWebhook')}
      subtitle={t(`webhookKindHint_${kind}` as MessageKey)}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={saving || !ready} onClick={submit}>
            {saving ? t('saving') : editing ? t('save') : t('addWebhook')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {editing ? null : (
          <Field label={t('webhookKind')} htmlFor="w-kind">
            <div id="w-kind">
              <Segmented
                label={t('webhookKind')}
                value={kind}
                onChange={setKind}
                items={WEBHOOK_KINDS.map((id) => ({ id, label: t(`webhookKind_${id}` as MessageKey) }))}
              />
            </div>
          </Field>
        )}

        <Field label={t('fullName')} htmlFor="w-name" hint={t('webhookNameHint')}>
          <Input id="w-name" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        {kind === 'JSON' ? (
          <Field label={t('endpoint')} htmlFor="w-url" hint={t('webhookUrlHint')}>
            <Input id="w-url" dir="ltr" placeholder="https://…" value={url} onChange={(event) => setUrl(event.target.value)} />
          </Field>
        ) : null}

        {kind === 'SLACK' ? (
          <Field label={t('slackUrl')} htmlFor="w-slack" hint={editing ? `${t('slackUrlHint')} ${t('keepCredentialHint')}` : t('slackUrlHint')}>
            <Input
              id="w-slack"
              dir="ltr"
              placeholder={editing ? endpoint.url : 'https://hooks.slack.com/services/…'}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          </Field>
        ) : null}

        {kind === 'TELEGRAM' ? (
          <>
            <Field label={t('telegramToken')} htmlFor="w-bot" hint={editing ? `${t('telegramTokenHint')} ${t('keepCredentialHint')}` : t('telegramTokenHint')}>
              <Input
                id="w-bot"
                dir="ltr"
                autoComplete="off"
                placeholder={editing ? endpoint.url.replace(/^.*\/bot/, '') : '123456789:AA…'}
                value={botToken}
                onChange={(event) => setBotToken(event.target.value)}
              />
            </Field>
            <Field label={t('telegramChat')} htmlFor="w-chat" hint={t('telegramChatHint')}>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Input id="w-chat" dir="ltr" placeholder="-1001234567890" value={chatId} onChange={(event) => setChatId(event.target.value)} />
                  <Button variant="secondary" disabled={finding || botToken.trim().length < 20} onClick={() => void findChats()}>
                    {finding ? t('loading') : t('telegramFindChats')}
                  </Button>
                </div>
                {chatError ? <Alert>{chatError}</Alert> : null}
                {chats && chats.length === 0 ? <p className="text-xs text-ink-muted">{t('telegramNoChats')}</p> : null}
                {chats && chats.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    {chats.map((chat) => (
                      <label key={chat.id} className="flex items-center gap-2 text-sm text-ink">
                        <input
                          type="radio"
                          name="w-chat-choice"
                          checked={chatId === chat.id}
                          onChange={() => setChatId(chat.id)}
                          className="size-4 accent-brand-600"
                        />
                        <span dir="auto">{chat.title}</span>
                        <span dir="ltr" className="font-mono text-xs text-ink-faint">
                          {chat.id}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            </Field>
          </>
        ) : null}

        {kind === 'JSON' ? null : (
          <Field label={t('webhookLanguage')} htmlFor="w-locale">
            <div id="w-locale">
              <Segmented
                label={t('webhookLanguage')}
                value={locale}
                onChange={setLocale}
                items={[
                  { id: 'ar', label: t('arabic') },
                  { id: 'en', label: t('english') },
                ]}
              />
            </div>
          </Field>
        )}
      </div>

      <fieldset className="mt-5 rounded-card border border-line p-4">
        <legend className="px-1 text-sm font-medium text-ink">{t('subscribedEvents')}</legend>
        <p className="mb-3 text-xs text-ink-muted">{t('subscribedEventsHint')}</p>

        <div className="grid gap-4 sm:grid-cols-2">
          {groups.map((group) => (
            <div key={group.area} className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold text-ink-muted">{labelOr(t, `webhookEventGroup_${group.area}`, group.area)}</p>
              {group.events.map((event) => (
                <label key={event} className="flex items-start gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={selected.includes(event)}
                    onChange={() =>
                      setSelected((current) => (current.includes(event) ? current.filter((entry) => entry !== event) : [...current, event]))
                    }
                    className="mt-0.5 size-4 rounded border-line accent-brand-600"
                  />
                  <span className="flex flex-col">
                    <span>{eventLabel(t, event)}</span>
                    <span dir="ltr" className="font-mono text-[11px] text-ink-faint">
                      {event}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      </fieldset>
    </Sheet>
  );
}
